import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Upload, X, File, Image, FileText } from 'lucide-react';
import './FileUpload.css';

export default function FileUpload({
    label,
    accept,
    maxSize = 10 * 1024 * 1024, // 10MB
    multiple = false,
    onChange,
    error,
    helperText,
    className = ''
}) {
    const [files, setFiles] = useState([]);
    const [dragActive, setDragActive] = useState(false);
    const inputRef = useRef(null);

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getFileIcon = (type) => {
        if (type.startsWith('image/')) return Image;
        if (type === 'application/pdf') return FileText;
        return File;
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const validateFile = (file) => {
        if (file.size > maxSize) {
            return `File size must be less than ${formatFileSize(maxSize)}`;
        }
        if (accept) {
            const allowedTypes = accept.split(',').map(t => t.trim());
            const isAllowed = allowedTypes.some(type => {
                if (type.startsWith('.')) {
                    return file.name.toLowerCase().endsWith(type);
                }
                if (type.endsWith('/*')) {
                    return file.type.startsWith(type.replace('/*', '/'));
                }
                return file.type === type;
            });
            if (!isAllowed) {
                return 'File type not allowed';
            }
        }
        return null;
    };

    const handleFiles = (fileList) => {
        const newFiles = Array.from(fileList).map(file => {
            const error = validateFile(file);
            return { file, error, id: Math.random().toString(36).substr(2, 9) };
        });

        const updatedFiles = multiple ? [...files, ...newFiles] : newFiles;
        setFiles(updatedFiles);
        onChange?.(updatedFiles.filter(f => !f.error).map(f => f.file));
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFiles(e.dataTransfer.files);
        }
    };

    const handleChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            handleFiles(e.target.files);
        }
    };

    const removeFile = (id) => {
        const updatedFiles = files.filter(f => f.id !== id);
        setFiles(updatedFiles);
        onChange?.(updatedFiles.filter(f => !f.error).map(f => f.file));
    };

    return (
        <div className={`file-upload-wrapper ${className}`}>
            {label && <label className="file-upload-label">{label}</label>}

            <div
                className={`file-upload-dropzone ${dragActive ? 'active' : ''} ${error ? 'error' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => inputRef.current?.click()}
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept={accept}
                    multiple={multiple}
                    onChange={handleChange}
                    className="file-upload-input"
                />
                <Upload size={32} className="file-upload-icon" />
                <p className="file-upload-text">
                    <span>Drag & drop files here, or click to browse</span>
                </p>
                <p className="file-upload-hint">
                    {accept ? `Accepted: ${accept}` : 'All file types accepted'} • Max {formatFileSize(maxSize)}
                </p>
            </div>

            {files.length > 0 && (
                <div className="file-upload-list">
                    {files.map(({ file, error: fileError, id }) => {
                        const Icon = getFileIcon(file.type);
                        return (
                            <div key={id} className={`file-upload-item ${fileError ? 'error' : ''}`}>
                                <Icon size={20} className="file-item-icon" />
                                <div className="file-item-info">
                                    <span className="file-item-name">{file.name}</span>
                                    <span className={`file-item-size ${fileError ? 'error' : ''}`}>
                                        {fileError || formatFileSize(file.size)}
                                    </span>
                                </div>
                                <button className="file-item-remove" onClick={() => removeFile(id)}>
                                    <X size={16} />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}

            {(error || helperText) && (
                <span className={`file-upload-helper ${error ? 'error' : ''}`}>
                    {error || helperText}
                </span>
            )}
        </div>
    );
}

FileUpload.propTypes = {
    label: PropTypes.string,
    accept: PropTypes.string,
    maxSize: PropTypes.number,
    multiple: PropTypes.bool,
    onChange: PropTypes.func,
    error: PropTypes.string,
    helperText: PropTypes.string,
    className: PropTypes.string
};
